'use client';

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Select from 'react-select';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DialogFooter } from '@/components/ui/dialog';
import { useGetCNCBrandsQuery } from '@/redux/cncBrand/cncBrand.api.slice';
import { useLazyGetCNCModelsQuery } from '@/redux/cncModel/cncModel.api.slice';
import { useLazyGetCNCVariantsQuery } from '@/redux/cncVariant/cncVariant.api.slice';
import { CNCBrandI } from '@/redux/cncBrand/cncBrand';

// Define the schema for validation
const machineSchema = z.object({
  cncBrand: z.string().min(1, 'CNC Brand is required'),
  cncModel: z.string().min(1, 'CNC Model is required'),
  cncVariant: z.string().min(1, 'CNC Variant is required'), // Make cncVariant required
});

// Props interface
interface MachineFormProps {
  onSubmit: (data: {
    cncVariantId: string;
    cncBrandName: string;
    cncModelName: string;
    cncVariantName: string;
  }) => void;
  onCancel: () => void;
}

export default function MachineForm({ onSubmit, onCancel }: MachineFormProps) {
  // Use the API queries
  const { data: cncBrandsData, isLoading: isLoadingCNCBrands } =
    useGetCNCBrandsQuery();
  const [getCNCModels, { isLoading: isLoadingModels }] =
    useLazyGetCNCModelsQuery();
  const [getCNCVariants, { isLoading: isLoadingVariants }] =
    useLazyGetCNCVariantsQuery();

  // State for selected item names
  const [selectedBrandName, setSelectedBrandName] = useState<string>('');
  const [selectedModelName, setSelectedModelName] = useState<string>('');
  const [selectedVariantName, setSelectedVariantName] = useState<string>('');

  // State for model and variant options
  const [cncModelOptions, setCncModelOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [cncVariantOptions, setCncVariantOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // Initialize form
  const form = useForm({
    resolver: zodResolver(machineSchema),
  });

  // Helper function to convert CNC brands to options format
  const getOptionsFromCncBrands = (cncBrands: CNCBrandI[] | undefined) => {
    if (!cncBrands) return [];
    return cncBrands.map((brand) => ({
      label: brand.name,
      value: brand._id,
    }));
  };

  // Function to fetch CNC models
  const fetchCNCModels = useCallback(
    async (brandId: string) => {
      if (!brandId) {
        setCncModelOptions([]);
        return;
      }

      try {
        const cncModels = await getCNCModels([brandId]).unwrap();
        const options = cncModels.map((model) => ({
          label: model.name,
          value: model._id,
        }));
        setCncModelOptions(options);
      } catch (error) {
        setCncModelOptions([]);
      }
    },
    [getCNCModels]
  );

  // Function to fetch CNC variants
  const fetchCNCVariants = useCallback(
    async (modelId: string) => {
      if (!modelId) {
        setCncVariantOptions([]);
        return;
      }

      try {
        const response = await getCNCVariants({
          modelIdList: [modelId],
        }).unwrap();

        // Using the response structure from your example
        const options = response.map((variant) => ({
          label: variant.name,
          value: variant._id,
        }));
        setCncVariantOptions(options);
      } catch (error) {
        setCncVariantOptions([]);
      }
    },
    [getCNCVariants]
  );

  // Watch for changes in brand and model selections
  const selectedBrand = form.watch('cncBrand');
  const selectedModel = form.watch('cncModel');
  const selectedVariant = form.watch('cncVariant');
  // Handle form submission with names
  const handleFormSubmit = () => {
    // Add names to the submission data
    onSubmit({
      cncVariantId: selectedVariant,
      cncBrandName: selectedBrandName,
      cncModelName: selectedModelName,
      cncVariantName: selectedVariantName,
    });
  };

  // Fetch models when brand changes
  useEffect(() => {
    if (selectedBrand) {
      fetchCNCModels(selectedBrand);
    }
  }, [fetchCNCModels, selectedBrand]);

  // Fetch variants when model changes
  useEffect(() => {
    if (selectedModel) {
      fetchCNCVariants(selectedModel);
    }
  }, [fetchCNCVariants, selectedModel]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="cncBrand"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CNC Brand</FormLabel>
              <FormControl>
                <Select
                  isLoading={isLoadingCNCBrands}
                  options={getOptionsFromCncBrands(cncBrandsData)}
                  value={
                    field.value
                      ? getOptionsFromCncBrands(cncBrandsData)?.find(
                          (option) => option.value === field.value
                        )
                      : null
                  }
                  onChange={(newValue) => {
                    field.onChange(newValue?.value || '');
                    setSelectedBrandName(newValue?.label || '');

                    // Reset dependent fields
                    form.setValue('cncModel', '');
                    form.setValue('cncVariant', '');
                    setSelectedModelName('');
                    setSelectedVariantName('');
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select CNC brand"
                  isSearchable
                  isClearable
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cncModel"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CNC Model</FormLabel>
              <FormControl>
                <Select
                  isLoading={isLoadingModels}
                  options={cncModelOptions}
                  value={
                    field.value
                      ? cncModelOptions.find(
                          (option) => option.value === field.value
                        )
                      : null
                  }
                  onChange={(newValue) => {
                    field.onChange(newValue?.value || '');
                    setSelectedModelName(newValue?.label || '');

                    // Reset dependent field
                    form.setValue('cncVariant', '');
                    setSelectedVariantName('');
                  }}
                  isDisabled={!selectedBrand}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select CNC model"
                  isSearchable
                  isClearable
                  onFocus={() => selectedBrand && fetchCNCModels(selectedBrand)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cncVariant"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CNC Variant</FormLabel>
              <FormControl>
                <Select
                  isLoading={isLoadingVariants}
                  options={cncVariantOptions}
                  value={
                    field.value
                      ? cncVariantOptions.find(
                          (option) => option.value === field.value
                        )
                      : null
                  }
                  onChange={(newValue) => {
                    field.onChange(newValue?.value || '');
                    setSelectedVariantName(newValue?.label || '');
                  }}
                  isDisabled={!selectedModel}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select CNC variant"
                  isSearchable
                  isClearable
                  onFocus={() =>
                    selectedModel && fetchCNCVariants(selectedModel)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter className="pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
